<!-- Feedback button (Hotjar style) -->
<button
  *ngIf="!hideButton"
  (click)="openModal()"
  class="fixed right-0 top-1/2 transform -translate-y-1/2 bg-white hover:bg-gray-100 text-black rounded-l-md shadow-lg transition-colors z-50 dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-white feedback-tab font-semibold"
>
  <div class="sideways-text">Feedback</div>
</button>

<!-- Use the shared modal component -->
<app-modal [isOpen]="isModalOpen" (isOpenChange)="isModalOpen = $event">
  <h2 class="text-xl font-bold mb-4 dark:text-white">Share your feedback</h2>

  <!-- Error message -->
  <div
    *ngIf="submitError"
    class="mb-4 p-3 bg-red-100 text-red-800 dark:bg-red-800/30 dark:text-red-200 rounded"
  >
    There was an error submitting your feedback. Please try again.
  </div>

  <!-- Feedback form -->
  <form [formGroup]="feedbackForm" (ngSubmit)="submitFeedback()">
    <!-- Subject dropdown -->
    <div class="mb-4">
      <label class="block text-sm font-medium mb-1 dark:text-white">
        Please select a subject
      </label>
      <select
        formControlName="subject"
        class="w-full p-2 border border-gray-300 rounded dark:bg-gray-800 dark:border-gray-600 dark:text-white text-sm sm:text-base"
      >
        <option *ngFor="let subject of subjects" [value]="subject">
          {{ subject }}
        </option>
      </select>
    </div>

    <!-- Message textarea -->
    <div class="mb-4">
      <label class="block text-sm font-medium mb-1 dark:text-white">
        What would you like to share?
      </label>
      <textarea
        formControlName="message"
        rows="4"
        class="w-full p-2 border border-gray-300 rounded focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-600 focus:border-slate-600 dark:focus:border-slate-600 dark:bg-gray-800 dark:border-gray-600 dark:text-white dark:focus-visible:ring-purple-500 text-sm sm:text-base"
      ></textarea>
      <div
        *ngIf="
          feedbackForm.get('message')?.invalid &&
          feedbackForm.get('message')?.touched
        "
        class="text-sm text-red-500 mt-1"
      >
        Please enter your feedback
      </div>
    </div>

    <!-- Contact permission (switch style) -->
    <div class="mb-4">
      <label class="flex items-center cursor-pointer">
        <label class="relative inline-flex items-center cursor-pointer mr-3">
          <input
            type="checkbox"
            formControlName="wants_contact"
            class="sr-only peer"
          />
          <div
            class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:!bg-gradient-to-r peer-checked:!from-[#a855f7] peer-checked:!to-[#ec4899] peer-checked:hover:!opacity-90"
          ></div>
        </label>
        <span class="text-sm dark:text-gray-300">
          I'm happy to be contacted about my feedback
        </span>
      </label>
    </div>

    <!-- Email field (conditional) -->
    <div class="mb-4" *ngIf="feedbackForm.get('wants_contact')?.value">
      <label class="block text-sm font-medium mb-1 dark:text-white">
        Email
      </label>
      <input
        type="email"
        formControlName="email"
        class="w-full p-2 border border-gray-300 rounded focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-600 focus:border-slate-600 dark:focus:border-slate-600 dark:bg-gray-800 dark:border-gray-600 dark:text-white dark:focus-visible:ring-purple-500 text-sm sm:text-base"
        placeholder="your@email.com"
      />
      <div
        *ngIf="
          feedbackForm.get('email')?.invalid &&
          feedbackForm.get('email')?.touched
        "
        class="text-sm text-red-500 mt-1"
      >
        Please enter a valid email address
      </div>
    </div>

    <!-- Action buttons -->
    <div class="flex justify-end space-x-2 mt-6">
      <button
        type="button"
        (click)="closeModal()"
        class="btn-secondary"
      >
        Cancel
      </button>
      <button
        type="submit"
        [disabled]="!feedbackForm.valid || isSubmitting"
        class="btn-primary disabled:bg-gray-500 flex items-center"
      >
        <span *ngIf="isSubmitting" class="mr-2">
          <svg
            class="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            ></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        </span>
        Submit
      </button>
    </div>
  </form>
</app-modal>

<!-- Toast Notification -->
<div 
  *ngIf="showToast"
  class="fixed bottom-4 left-4 z-[150] animate-slide-up">
  <div class="bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 max-w-sm">
    <svg class="w-5 h-5 text-green-400 dark:text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
    </svg>
    <p class="text-sm font-medium">{{ toastMessage }}</p>
    <button 
      (click)="showToast = false"
      class="ml-2 text-gray-400 dark:text-gray-600 hover:text-white dark:hover:text-gray-900">
      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path>
      </svg>
    </button>
  </div>
</div>
